import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import DocAnnotationLayer from './DocAnnotationLayer';
import './CafeDocViewer.css';

// Set up pdf.js worker (use CDN for simplicity)
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const CafeDocViewer = ({
  docTitle = 'No document selected',
  page = 1,
  docs = [],
  selectedDocId = null,
  onSelectDoc,
  docUrl,
  docType,       // 'pdf' | 'image' | undefined (detected from url if not provided)
  docRotation = 0,
  onToggleDocRotation,
  onPageChange,
  onTotalPagesChange, // callback to notify parent of total pages
  onUploadDoc,
  onDeleteDoc,
  isPresenter = false, // only presenter can change pages
  mountedDocs = [],
  activeDocId = null,
  showDocsTray = false,
  onToggleDocsTray,
  onUnmountDoc,
  // Annotation props
  roomId,
  annotations = [],
  onAddAnnotation,
  onDeleteAnnotation,
  onUpdateAnnotation,
  supabase,
  userId,
}) => {
  const selectedDoc = docs.find(doc => String(doc.id) === String(selectedDocId));
  const canDeleteSelectedDoc = !!(selectedDoc && userId && String(selectedDoc.owner_id) === String(userId));

  const mountedDocModels = useMemo(() => {
    return mountedDocs
      .map((rd) => {
        const doc = docs.find(d => String(d.id) === String(rd.document_id)) || null;
        return { roomDoc: rd, doc };
      })
      .filter(x => x.doc);
  }, [mountedDocs, docs]);

  const [trayPreviewUrls, setTrayPreviewUrls] = useState({});

  useEffect(() => {
    if (!showDocsTray) return;
    if (!supabase) return;

    let cancelled = false;

    const next = {};
    mountedDocModels.forEach(({ roomDoc, doc }) => {
      if (!doc?.id) return;

      const vs = roomDoc?.view_state || {};
      const roomPreviewBucket = vs?.preview_thumbnail_bucket || null;
      const roomPreviewPath = vs?.preview_thumbnail_path || null;
      const roomPreviewPage = typeof vs?.preview_page === 'number' ? vs.preview_page : null;
      const roomPreviewVersion = typeof vs?.preview_version === 'number' ? vs.preview_version : null;

      const isImageDoc =
        (typeof doc.mime_type === 'string' && doc.mime_type.startsWith('image/')) ||
        (typeof doc.path === 'string' && /\.(jpe?g|png|gif|bmp|webp|svg)$/i.test(doc.path)) ||
        (typeof doc.title === 'string' && /\.(jpe?g|png|gif|bmp|webp|svg)$/i.test(doc.title));

      // Only use doc.path as a preview when it's an image.
      // For PDFs, we require a generated thumbnail; otherwise the <img> will break.
      const bucket =
        roomPreviewBucket ||
        doc.thumbnail_bucket ||
        (isImageDoc ? (doc.bucket || 'documents') : null);
      const path =
        roomPreviewPath ||
        doc.thumbnail_path ||
        (isImageDoc ? doc.path : null);
      if (!bucket || !path) return;

      try {
        const { data } = supabase
          .storage
          .from(bucket)
          .getPublicUrl(path);

        if (data?.publicUrl) {
          const url = data.publicUrl;
          const version = roomPreviewPath ? (roomPreviewVersion ?? roomPreviewPage ?? Date.now()) : null;
          next[String(doc.id)] = version ? `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(String(version))}` : url;
        }
      } catch {
        // ignore
      }
    });

    if (!cancelled) {
      setTrayPreviewUrls(prev => {
        let changed = false;
        const merged = { ...prev };
        Object.entries(next).forEach(([k, v]) => {
          if (!merged[k] || merged[k] !== v) {
            merged[k] = v;
            changed = true;
          }
        });
        return changed ? merged : prev;
      });
    }

    return () => {
      cancelled = true;
    };
  }, [showDocsTray, mountedDocModels, supabase]);

  useEffect(() => {
    if (!showDocsTray) return;
    if (!onToggleDocsTray) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onToggleDocsTray();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showDocsTray, onToggleDocsTray]);

  const handleTraySwitch = (docId) => {
    if (!docId) return;
    if (!onSelectDoc) return;
    if (!isPresenter) return;
    onSelectDoc(docId);
    if (onToggleDocsTray) onToggleDocsTray();
  };

  const handleTrayItemKeyDown = (e, docId) => {
    if (!isPresenter) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleTraySwitch(docId);
    }
  };
  const [numPages, setNumPages] = useState(1);
  const [pageWidth, setPageWidth] = useState(600);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const canvasContainerRef = useRef(null);
  
  // Annotation tool state
  const [activeTool, setActiveTool] = useState(null); // 'pointer' | 'pen' | 'eraser' | 'line' | 'rect' | 'circle' | 'text'
  const [toolColor, setToolColor] = useState('#ff0000');
  const [toolSize, setToolSize] = useState(3);
  const [shapeSize, setShapeSize] = useState(3);
  const [fillShapes, setFillShapes] = useState(false);
  
  // Popover menus for pen/eraser size
  const [showPenMenu, setShowPenMenu] = useState(false);
  const [showEraserMenu, setShowEraserMenu] = useState(false);
  const [showShapesMenu, setShowShapesMenu] = useState(false);
  
  // Undo/redo stacks for annotations (local state, page-scoped)
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);

  // Detect document type from URL or explicit prop
  const detectedType = docType || (docUrl && /\.(jpe?g|png|gif|bmp|webp|svg)$/i.test(docUrl) ? 'image' : 'pdf');
  const isPdf = detectedType === 'pdf';
  const isImage = detectedType === 'image';
  const hasDoc = !!docUrl;

  const normalizedRotation = docRotation === 90 ? 90 : 0;

  // Responsive: fit page width to container
  useEffect(() => {
    const updateWidth = () => {
      if (canvasContainerRef.current) {
        const w = canvasContainerRef.current.clientWidth - 32; // padding
        setPageWidth(Math.max(300, Math.min(w, 900)));
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Reset state when document changes
  useEffect(() => {
    setNumPages(isImage ? 1 : 1);
    setError(null);
    setLoading(isPdf && hasDoc);
  }, [docUrl, isPdf, isImage, hasDoc]);

  // Notify parent of total pages
  useEffect(() => {
    if (onTotalPagesChange) {
      onTotalPagesChange(numPages);
    }
  }, [numPages, onTotalPagesChange]);

  const handleSelectChange = (e) => {
    const value = e.target.value || null;
    if (!value || !onSelectDoc) return;
    onSelectDoc(value);
  };

  const handlePrev = () => {
    if (onPageChange && hasDoc && isPresenter && page > 1) onPageChange(-1);
  };

  const handleNext = () => {
    if (onPageChange && hasDoc && isPresenter && page < numPages) onPageChange(1);
  };

  const onDocumentLoadSuccess = ({ numPages: n }) => {
    setNumPages(n);
    setLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (err) => {
    console.error('PDF load error:', err);
    setLoading(false);
    setError('Failed to load PDF');
  };

  // Clamp page to valid range
  const currentPage = Math.max(1, Math.min(page, numPages));

  // Get current page annotations
  const currentPageAnnotations = annotations.filter(a => a.page_number === currentPage);

  // Undo handler - delete most recent annotation on this page
  const handleUndo = () => {
    if (currentPageAnnotations.length === 0) return;
    const lastAnn = currentPageAnnotations[currentPageAnnotations.length - 1];
    if (lastAnn && onDeleteAnnotation) {
      undoStackRef.current.push(lastAnn);
      onDeleteAnnotation(lastAnn.id);
    }
  };

  // Redo handler - re-add last undone annotation
  const handleRedo = () => {
    const ann = undoStackRef.current.pop();
    if (ann && onAddAnnotation) {
      // Re-insert without the id so it gets a new one
      const { id, ...rest } = ann;
      onAddAnnotation(rest);
    }
  };

  // Clear all annotations on current page
  const handleClear = () => {
    currentPageAnnotations.forEach(ann => {
      if (onDeleteAnnotation) {
        undoStackRef.current.push(ann);
        onDeleteAnnotation(ann.id);
      }
    });
  };

  return (
    <div className="cafe-doc-viewer">
      <div className="cafe-doc-header">
        <div className="cafe-doc-header-left">
          {docs.length > 0 ? (
            <>
              <select
                className="cafe-doc-select"
                value={selectedDocId || ''}
                onChange={handleSelectChange}
                disabled={!isPresenter}
              >
                <option value="" disabled>
                  Select a document
                </option>
                {docs.map(doc => (
                  <option key={doc.id} value={doc.id}>
                    {doc.title || doc.path}
                  </option>
                ))}
              </select>
              {!!onToggleDocsTray && (
                <button
                  type="button"
                  className="cafe-doc-upload"
                  onClick={onToggleDocsTray}
                  title="Docs tray"
                  aria-label="Toggle docs tray"
                >
                  ▤
                </button>
              )}
              {selectedDocId && onDeleteDoc && (
                <button
                  type="button"
                  className="cafe-doc-upload"
                  onClick={() => onDeleteDoc(selectedDocId)}
                  disabled={!canDeleteSelectedDoc}
                  title={canDeleteSelectedDoc ? 'Remove selected document' : 'Only the owner can delete this document'}
                  aria-label="Delete document"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14H6L5 6" />
                    <path d="M10 11v6" />
                    <path d="M14 11v6" />
                    <path d="M9 6V4h6v2" />
                  </svg>
                </button>
              )}
            </>
          ) : (
            <span className="cafe-doc-title">{docTitle}</span>
          )}
        </div>
        {isPresenter && (
          <button
            type="button"
            className="cafe-doc-upload"
            onClick={onUploadDoc}
          >
            Upload
          </button>
        )}

        {isPresenter && hasDoc && !!onToggleDocRotation && (
          <button
            type="button"
            className="cafe-doc-upload"
            onClick={onToggleDocRotation}
            title="Rotate (portrait / landscape)"
            aria-label="Rotate"
          >
            ⤾
          </button>
        )}
      </div>

      {showDocsTray && (
        <div
          className="cafe-docs-tray-overlay"
          role="dialog"
          aria-label="Docs tray"
          onClick={(e) => {
            if (e.target === e.currentTarget && onToggleDocsTray) onToggleDocsTray();
          }}
        >
          <div className="cafe-docs-tray" onClick={(e) => e.stopPropagation()}>
            {!!onToggleDocsTray && (
              <button
                type="button"
                className="cafe-docs-tray-x"
                onClick={onToggleDocsTray}
                aria-label="Close"
                title="Close"
              >
                ×
              </button>
            )}
            <div className="cafe-docs-tray-row">
              {mountedDocModels.map(({ roomDoc, doc }) => {
                const isActive = String(activeDocId || '')
                  ? String(activeDocId) === String(doc.id)
                  : String(selectedDocId || '') === String(doc.id);
                const label = doc.title || doc.filename || doc.path || 'Document';
                const previewUrl = trayPreviewUrls[String(doc.id)] || null;
                return (
                  <div
                    key={doc.id}
                    className={`cafe-docs-tray-item ${isActive ? 'active' : ''} ${isPresenter ? '' : 'disabled'}`}
                    onClick={() => handleTraySwitch(doc.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => handleTrayItemKeyDown(e, doc.id)}
                  >
                    <div className="cafe-docs-tray-thumb">
                      {!previewUrl && (
                        <div className="cafe-docs-tray-thumb-fallback">📄</div>
                      )}

                      {!!previewUrl && (
                        <img
                          className="cafe-docs-tray-thumb-img"
                          src={previewUrl}
                          alt={label}
                          loading="lazy"
                        />
                      )}
                    </div>

                    <div className="cafe-docs-tray-item-meta">
                      <div className="cafe-docs-tray-item-title">{label}</div>
                      {typeof roomDoc?.last_page === 'number' && (
                        <div className="cafe-docs-tray-item-sub">p. {roomDoc.last_page || 1}</div>
                      )}
                    </div>
                    {isPresenter && onUnmountDoc && (
                      <button
                        type="button"
                        className="cafe-docs-tray-item-remove"
                        onClick={(e) => { e.stopPropagation(); onUnmountDoc(doc.id); }}
                        title="Unmount"
                        aria-label="Unmount"
                      >
                        ×
                      </button>
                    )}
                  </div>
                );
              })}

              {isPresenter && onUploadDoc && (
                <button
                  type="button"
                  className="cafe-docs-tray-item cafe-docs-tray-add"
                  onClick={onUploadDoc}
                  title="Mount another document"
                >
                  +
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="cafe-doc-canvas" ref={canvasContainerRef}>
        {isPresenter && (
          <div className="cafe-doc-annotation-panel">
            {/* Pointer */}
            <button
              className={`panel-btn ${activeTool === 'pointer' ? 'active' : ''}`}
              onClick={() => setActiveTool(activeTool === 'pointer' ? null : 'pointer')}
              title="Pointer"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
              </svg>
            </button>
            {/* Pen with size menu */}
            <div className="panel-tool-wrap">
              <button
                className={`panel-btn ${activeTool === 'pen' ? 'active' : ''}`}
                onClick={() => { setActiveTool('pen'); setShowPenMenu(s => !s); setShowEraserMenu(false); setShowShapesMenu(false); }}
                title="Pen"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5l4 4L7 21H3v-4L16.5 3.5z" />
                </svg>
              </button>
              {showPenMenu && (
                <div className="panel-popover">
                  {[2, 4, 8, 12, 20].map(s => (
                    <button key={s} className="panel-swatch" onClick={() => { setToolSize(s); setShowPenMenu(false); }} title={`${s}px`}>
                      <span style={{ height: s, background: toolColor, width: '100%', borderRadius: 2 }} />
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Eraser with size menu */}
            <div className="panel-tool-wrap">
              <button
                className={`panel-btn ${activeTool === 'eraser' ? 'active' : ''}`}
                onClick={() => { setActiveTool('eraser'); setShowEraserMenu(s => !s); setShowPenMenu(false); setShowShapesMenu(false); }}
                title="Eraser"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 3l5 5-9 9H7l-4-4 9-9z" />
                  <path d="M6 17h7" />
                </svg>
              </button>
              {showEraserMenu && (
                <div className="panel-popover">
                  {[6, 10, 16, 24, 32].map(s => (
                    <button key={s} className="panel-swatch" onClick={() => { setToolSize(s); setShowEraserMenu(false); }} title={`${s}px`}>
                      <span style={{ height: s, background: 'linear-gradient(90deg, rgba(0,0,0,0) 0%, #94a3b8 100%)', width: '100%', borderRadius: 2 }} />
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Shapes with shape selector (line, rect, square, circle, arrow) */}
            <div className="panel-tool-wrap">
              <button
                className={`panel-btn ${['line', 'rect', 'square', 'circle', 'arrow'].includes(activeTool) ? 'active' : ''}`}
                onClick={() => { setShowShapesMenu(s => !s); setShowPenMenu(false); setShowEraserMenu(false); }}
                title="Shapes"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <circle cx="17" cy="7" r="3" />
                  <path d="M14 14l7 7H7z" />
                </svg>
              </button>
              {showShapesMenu && (
                <div className="panel-popover">
                  <button className="panel-swatch" onClick={() => { setActiveTool('line'); setShowShapesMenu(false); }} title="Line">
                    ／
                  </button>
                  <button className="panel-swatch" onClick={() => { setActiveTool('rect'); setShowShapesMenu(false); }} title="Rectangle">
                    ▭
                  </button>
                  <button className="panel-swatch" onClick={() => { setActiveTool('square'); setShowShapesMenu(false); }} title="Square">
                    ◼︎
                  </button>
                  <button className="panel-swatch" onClick={() => { setActiveTool('circle'); setShowShapesMenu(false); }} title="Circle">
                    ◯
                  </button>
                  <button className="panel-swatch" onClick={() => { setActiveTool('arrow'); setShowShapesMenu(false); }} title="Arrow">
                    ➤
                  </button>
                </div>
              )}
              <input
                type="range"
                min={1}
                max={24}
                value={shapeSize}
                onChange={(e) => setShapeSize(Number(e.target.value))}
                className="size-slider"
                title="Shape width"
              />
            </div>
            {/* Select tool (shapes and text) */}
            <button
              className={`panel-btn ${activeTool === 'select' ? 'active' : ''}`}
              title="Select / move"
              onClick={() => { setActiveTool('select'); setShowPenMenu(false); setShowEraserMenu(false); setShowShapesMenu(false); }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {/* Dotted square */}
                <rect x="5" y="5" width="14" height="14" rx="2" strokeDasharray="3 2" />
                {/* 4-way arrows */}
                <path d="M12 8v8M8 12h8" />
                <path d="M12 8l-2 2M12 8l2 2" />
                <path d="M12 16l-2-2M12 16l2-2" />
                <path d="M8 12l2-2M8 12l2 2" />
                <path d="M16 12l-2-2M16 12l-2 2" />
              </svg>
            </button>
            {/* Color picker */}
            <label className="panel-color" title="Color">
              <input
                type="color"
                value={toolColor}
                onChange={(e) => setToolColor(e.target.value)}
              />
            </label>
            {/* Fill toggle */}
            <label className="panel-fill" title="Fill shapes">
              <input
                type="checkbox"
                checked={fillShapes}
                onChange={(e) => setFillShapes(e.target.checked)}
              />
              <span>Fill</span>
            </label>
            {/* Undo */}
            <button className="panel-btn" onClick={handleUndo} title="Undo" disabled={currentPageAnnotations.length === 0}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 7v6h6" />
                <path d="M3 13a9 9 0 1 0 3-6" />
              </svg>
            </button>
            {/* Redo */}
            <button className="panel-btn" onClick={handleRedo} title="Redo" disabled={undoStackRef.current.length === 0}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 7v6h-6" />
                <path d="M21 13a9 9 0 1 1-3-6" />
              </svg>
            </button>
            {/* Clear */}
            <button className="panel-btn" onClick={handleClear} title="Clear page annotations" disabled={currentPageAnnotations.length === 0}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18" />
                <path d="M8 6V4h8v2" />
                <path d="M19 6l-1 14H6L5 6" />
              </svg>
            </button>
            {/* Page navigation */}
            {hasDoc && numPages > 1 && (
              <div className="panel-page-nav">
                <button
                  className="panel-btn"
                  onClick={handlePrev}
                  disabled={currentPage <= 1}
                  title="Previous page"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <div className="panel-page-indicator">{currentPage}/{numPages}</div>
                <button
                  className="panel-btn"
                  onClick={handleNext}
                  disabled={currentPage >= numPages}
                  title="Next page"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              </div>
            )}
            {/* Done button */}
            {activeTool && (
              <button
                className="panel-btn done"
                onClick={() => setActiveTool(null)}
                title="Done annotating"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </button>
            )}
          </div>
        )}
        {!hasDoc && (
          <div className="cafe-doc-placeholder">
            <div className="cafe-doc-placeholder-main">No document selected</div>
            <div className="cafe-doc-placeholder-sub">
              {isPresenter
                ? 'Use the Upload button above to add a document and then select it from the list.'
                : 'Waiting for the presenter to select a document.'}
            </div>
          </div>
        )}

        {hasDoc && isPdf && (
          <div className="cafe-doc-pdf-wrapper">
            {loading && <div className="cafe-doc-loading">Loading PDF...</div>}
            {error && <div className="cafe-doc-error">{error}</div>}
            <div className="cafe-doc-page-container">
              <Document
                file={docUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading=""
                className="cafe-doc-pdf-document"
              >
                <Page
                  pageNumber={currentPage}
                  width={pageWidth}
                  rotate={normalizedRotation}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                />
              </Document>
              <DocAnnotationLayer
                documentId={selectedDocId}
                pageNumber={currentPage}
                roomId={roomId}
                annotations={currentPageAnnotations}
                isPresenter={isPresenter}
                onAddAnnotation={onAddAnnotation}
                onDeleteAnnotation={onDeleteAnnotation}
                onUpdateAnnotation={onUpdateAnnotation}
                activeTool={activeTool}
                toolColor={toolColor}
                toolSize={toolSize}
                shapeSize={shapeSize}
                fillShapes={fillShapes}
                supabase={supabase}
                userId={userId}
                rotation={normalizedRotation}
              />
            </div>
          </div>
        )}

        {hasDoc && isImage && (
          <div className="cafe-doc-image-wrapper">
            <div className="cafe-doc-page-container">
              <img
                src={docUrl}
                alt={docTitle}
                className={`cafe-doc-image ${normalizedRotation === 90 ? 'rot-90' : ''}`}
                onLoad={() => setLoading(false)}
                onError={() => setError('Failed to load image')}
              />
              <DocAnnotationLayer
                documentId={selectedDocId}
                pageNumber={1}
                roomId={roomId}
                annotations={annotations.filter(a => a.page_number === 1)}
                isPresenter={isPresenter}
                onAddAnnotation={onAddAnnotation}
                onDeleteAnnotation={onDeleteAnnotation}
                onUpdateAnnotation={onUpdateAnnotation}
                activeTool={activeTool}
                toolColor={toolColor}
                toolSize={toolSize}
                shapeSize={shapeSize}
                fillShapes={fillShapes}
                supabase={supabase}
                userId={userId}
                rotation={normalizedRotation}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CafeDocViewer;
